import type { ChangeEvent } from "react";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { inject, injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import type { InputOnChangeData } from "@/components/Input/Input";
import {
  MAX_PRODUCT_PHOTOS,
  normalizeProductPhotoState,
  removeProductPhoto,
} from "@/domain/product-photos";
import {
  extractQrPayload,
  normalizeQrPayloadInput,
  qrScanErrorToMessage,
  validateQrPayload,
} from "@/domain/qr-payload";
import {
  type BoxListItem,
  type IWarehouseApi,
  QrBindingConflictError,
  WAREHOUSE_API_TOKEN,
} from "@/api/WarehouseApi";
import { Measures } from "@/types";
import type { IStore } from "@/stores/store";
import { STORE_TOKEN } from "@/stores/store";
import {
  SETTINGS_STORE_TOKEN,
  type ISettingsStore,
} from "@/stores/SettingsStore";
import {
  formatBoxReferenceInputValue,
  formatBoxReferenceLabel,
} from "@/utils/boxReference";
import {
  type INavigationStore,
  NAVIGATION_STORE_TOKEN,
} from "@/stores/navigation";
import { isSpeechRecognitionSupported } from "@/utils/speechRecognition";
import { BOX_ID_PATTERN, type AddProductDraft } from "./viewmodel/form-state";
import { AddProductVoiceController } from "./viewmodel/voice-controller";
import {
  createAddProductVoiceController,
  type AddProductVoiceBindingState,
} from "./viewmodel/voice-binding";
import {
  addTypeTagsValue,
  removeTypeTagValue,
  setDescriptionValue,
  setExpiryDateInputValue,
  setManufacturingDateInputValue,
  setMeasureValue,
  setNameValue,
  setQuantityValue,
  setTypeTagInputValue,
  type AddProductInteractionState,
} from "./viewmodel/interactions";
import {
  applyInitialDraftOperation,
  applyPresetParentIdOperation,
  clearAddProductFormOperation,
  fillFromVoiceTranscriptOperation,
  goBackFromAddProductOperation,
  loadReferenceDataOperation,
  openCreateBoxWithReturnOperation,
  requestPhotoAutofillSuggestionOperation,
  setPhotoFromFileOperation,
  submitAddProductOperation,
  type AddProductPhotoAiSuggestion,
  type AddProductWorkflowState,
} from "./viewmodel/operations";
import { applyAiProductDraftToState } from "./viewmodel/page-actions";

export type { AddProductDraft } from "./viewmodel/form-state";

export interface AddProductPageViewProps {
  parentId?: string;
  draft?: AddProductDraft;
}

@injectable()
export class AddProductPageViewModel extends ViewModel<
  unknown,
  AddProductPageViewProps
> {
  @observable
  name = "";

  @observable
  description = "";

  @observable
  photo = "";

  @observable.shallow
  photos: string[] = [];

  @observable
  activePhotoIndex = 0;

  @observable
  typeTags: string[] = [];

  @observable
  typeTagInput = "";

  @observable
  parentId = "";

  @observable
  manufacturingDate = "";

  @observable
  expiryDate = "";

  @observable
  measure: Measures = Measures.quantity;

  @observable
  quantityInput = "1";

  @observable.shallow
  availableBoxes: BoxListItem[] = [];

  @observable.shallow
  typeTagSuggestions: string[] = [];

  @observable
  isReferenceLoading = false;

  @observable
  referenceError: string | null = null;

  @observable
  isPhotoLoading = false;

  @observable
  isSubmitting = false;

  @observable
  error: string | null = null;

  @observable
  qrPayloadInput = "";

  @observable
  qrBindingError: string | null = null;

  @observable
  isQrScannerOpen = false;

  @observable
  isQrScanResolving = false;

  @observable
  voiceTranscript = "";

  @observable
  isVoicePanelOpen = false;

  @observable
  isVoiceListening = false;

  @observable
  isVoiceApplying = false;

  @observable
  voiceError: string | null = null;

  @observable
  voiceInfo: string | null = null;

  @observable
  isVoiceSupported = isSpeechRecognitionSupported();

  @observable.ref
  aiPhotoSuggestion: AddProductPhotoAiSuggestion | null = null;

  private readonly presetParentId: string;
  private readonly initialDraft: AddProductDraft | null;
  private readonly voiceController: AddProductVoiceController;

  constructor(
    @inject(WAREHOUSE_API_TOKEN) private readonly api: IWarehouseApi,
    @inject(STORE_TOKEN) private readonly store: IStore,
    @inject(SETTINGS_STORE_TOKEN)
    private readonly settingsStore: ISettingsStore,
    @inject(NAVIGATION_STORE_TOKEN)
    private readonly navigationStore: INavigationStore,
  ) {
    super();

    this.presetParentId =
      this.viewProps?.parentId && BOX_ID_PATTERN.test(this.viewProps.parentId)
        ? this.viewProps.parentId
        : "";
    this.initialDraft = this.viewProps?.draft
      ? {
          ...this.viewProps.draft,
          photos: Array.isArray(this.viewProps.draft.photos)
            ? [...this.viewProps.draft.photos]
            : [],
          typeTags: [...this.viewProps.draft.typeTags],
        }
      : null;

    makeObservable(this);

    this.voiceController = createAddProductVoiceController({
      state: this.voiceBindingState,
    });
  }

  @computed
  get title(): string {
    return `добавляем ${this.name.trim() ? this.name.trim() : "что-то новое"}`;
  }

  @computed
  get hasPhotoPreview(): boolean {
    return this.photos.length > 0 || this.photo.trim().length > 0;
  }

  @computed
  get isAiPhotoAutofillEnabled(): boolean {
    return this.settingsStore.aiPhotoAutofillEnabled;
  }

  @computed
  get normalizedPhotos(): string[] {
    return normalizeProductPhotoState({
      photo: this.photo,
      photos: this.photos,
    }).photos;
  }

  @computed
  get photoCounterLabel(): string {
    return `Фото: ${this.normalizedPhotos.length}/${MAX_PRODUCT_PHOTOS}`;
  }

  @computed
  get canAddMorePhotos(): boolean {
    return (
      this.normalizedPhotos.length < MAX_PRODUCT_PHOTOS &&
      !this.isSubmitting &&
      !this.isPhotoLoading
    );
  }

  @computed
  get canRemoveActivePhoto(): boolean {
    return this.normalizedPhotos.length > 0 && !this.isSubmitting;
  }

  @computed
  get canSubmit(): boolean {
    return !this.isSubmitting && this.availableBoxes.length > 0;
  }

  @computed
  get qrScannerDelay(): number | false {
    if (!this.isQrScannerOpen || this.isSubmitting || this.isQrScanResolving) {
      return false;
    }

    return 300;
  }

  @computed
  get canStartVoiceListening(): boolean {
    return (
      this.isVoiceSupported &&
      !this.isVoiceListening &&
      !this.isVoiceApplying &&
      !this.isSubmitting
    );
  }

  @computed
  get canStopVoiceListening(): boolean {
    return this.isVoiceListening;
  }

  @computed
  get canApplyVoiceTranscript(): boolean {
    return (
      this.isVoicePanelOpen &&
      !this.isVoiceApplying &&
      !this.isSubmitting &&
      this.voiceTranscript.trim().length >= 4
    );
  }

  @computed
  get canApplyPhotoAutofill(): boolean {
    const normalized = normalizeProductPhotoState({
      photo: this.photo,
      photos: this.photos,
    });
    return (
      this.isAiPhotoAutofillEnabled &&
      !!normalized.photo.trim() &&
      !this.isVoiceApplying &&
      !this.isSubmitting
    );
  }

  @computed
  get isAiPhotoSuggestionOpen(): boolean {
    return !!this.aiPhotoSuggestion;
  }

  @computed
  get aiPhotoSuggestionDescription(): string {
    if (!this.aiPhotoSuggestion) {
      return "";
    }

    if (this.aiPhotoSuggestion.changes.length === 0) {
      return "AI не предложил изменений.";
    }

    const source = this.aiPhotoSuggestion.usedTranscriptContext
      ? "Источник: фото + текст диктовки"
      : "Источник: только фото";

    return [
      `Модель: ${this.aiPhotoSuggestion.model}`,
      source,
      "",
      "Предложенные изменения:",
      ...this.aiPhotoSuggestion.changes.map((line) => `- ${line}`),
    ].join("\n");
  }

  @computed
  get parentIdSuggestions(): Array<{ id: string; label: string }> {
    return this.availableBoxes.map((box) => ({
      id: box.id,
      label: formatBoxReferenceLabel(box),
    }));
  }

  protected onViewMounted(): void {
    applyInitialDraftOperation({
      state: this.workflowState,
      initialDraft: this.initialDraft,
    });
    applyPresetParentIdOperation({
      state: this.workflowState,
      presetParentId: this.presetParentId,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });
    const normalizedPhotoState = normalizeProductPhotoState({
      photo: this.photo,
      photos: this.photos,
    });
    this.photo = normalizedPhotoState.photo;
    this.photos = normalizedPhotoState.photos;
    this.activePhotoIndex = 0;
    this.aiPhotoSuggestion = null;
    void this.loadReferenceData();
  }

  protected onViewUnmounted(): void {
    this.voiceController.teardown();
  }

  @action
  onNameChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    setNameValue(this.interactionState, event, data);
  };

  @action
  onDescriptionChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    setDescriptionValue(this.interactionState, event, data);
  };

  @action.bound
  removePhoto(index: number): void {
    const nextState = removeProductPhoto(
      {
        photo: this.photo,
        photos: this.photos,
      },
      index,
    );
    this.photo = nextState.photo;
    this.photos = nextState.photos;
    this.activePhotoIndex = Math.max(
      0,
      Math.min(this.activePhotoIndex, this.photos.length - 1),
    );
    this.error = null;
  }

  @action.bound
  removeActivePhoto(): void {
    if (!this.canRemoveActivePhoto) {
      return;
    }

    this.removePhoto(this.activePhotoIndex);
  }

  @action.bound
  onPhotoSliderActiveIndexChange(index: number): void {
    this.activePhotoIndex = Math.max(
      0,
      Math.min(index, this.photos.length - 1),
    );
  }

  @action.bound
  async onPhotoFileChange(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    await setPhotoFromFileOperation({
      state: this.workflowState,
      event,
    });
    this.activePhotoIndex = 0;
  }

  @action
  setTypeTagInput = (value: string): void => {
    setTypeTagInputValue(this.interactionState, value);
  };

  @action.bound
  addTypeTags(tags: string[]): void {
    addTypeTagsValue(this.interactionState, tags);
  }

  @action.bound
  removeTypeTag(tag: string): void {
    removeTypeTagValue(this.interactionState, tag);
  }

  @action.bound
  setParentIdInput(value: string): void {
    this.parentId = value;
    this.error = null;
  }

  @action
  setManufacturingDateValue = (value: string): void => {
    setManufacturingDateInputValue(this.interactionState, value);
  };

  @action
  setExpiryDateValue = (value: string): void => {
    setExpiryDateInputValue(this.interactionState, value);
  };

  @action
  setMeasure = (measure: Measures): void => {
    setMeasureValue(this.interactionState, measure);
  };

  @action
  onQuantityChange = (
    event: ChangeEvent<HTMLInputElement>,
    data: InputOnChangeData,
  ): void => {
    setQuantityValue(this.interactionState, event, data);
  };

  @action.bound
  toggleQrScanner(): void {
    if (this.isSubmitting) {
      return;
    }

    this.isQrScannerOpen = !this.isQrScannerOpen;
    this.qrBindingError = null;
  }

  @action.bound
  async onQrScan(rawData: string | null): Promise<void> {
    if (!rawData || !this.isQrScannerOpen || this.isQrScanResolving) {
      return;
    }

    const payload = extractQrPayload(rawData);
    if (!payload) {
      this.qrBindingError =
        "Сканируйте QR-код из партии печати (формат qr_...).";
      return;
    }

    runInAction(() => {
      this.isQrScanResolving = true;
      this.qrBindingError = null;
    });

    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (existing) {
        runInAction(() => {
          this.qrPayloadInput = "";
          this.qrBindingError = this.formatQrConflictMessage(
            payload,
            existing.entityId,
          );
        });
        return;
      }

      runInAction(() => {
        this.qrPayloadInput = payload;
        this.qrBindingError = null;
        this.isQrScannerOpen = false;
      });
    } catch (error) {
      runInAction(() => {
        this.qrBindingError =
          error instanceof Error
            ? error.message
            : "Не удалось проверить QR-код";
      });
    } finally {
      runInAction(() => {
        this.isQrScanResolving = false;
      });
    }
  }

  @action.bound
  onQrScanError(error: unknown): void {
    this.qrBindingError = qrScanErrorToMessage(error);
  }

  @action
  onVoiceTranscriptChange = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    this.voiceController.setManualTranscript(event.target.value);
  };

  @action.bound
  onVoicePrimaryButtonClick(): void {
    this.voiceController.onPrimaryButtonClick();
  }

  @action.bound
  closeVoicePanel(): void {
    this.voiceController.closePanel();
  }

  @action.bound
  clearVoiceTranscript(): void {
    this.voiceController.clearTranscript();
  }

  @action.bound
  startVoiceListening(): void {
    this.voiceController.startListening();
  }

  @action.bound
  stopVoiceListening(): void {
    this.voiceController.stopListening();
  }

  @action.bound
  async fillFromVoiceTranscript(): Promise<void> {
    await fillFromVoiceTranscriptOperation({
      state: this.workflowState,
      api: this.api,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });
  }

  @action.bound
  async fillFromPhoto(): Promise<void> {
    if (!this.isAiPhotoAutofillEnabled) {
      this.voiceError = "AI-по-фото выключен в настройках приложения";
      return;
    }

    const suggestion = await requestPhotoAutofillSuggestionOperation({
      state: this.workflowState,
      api: this.api,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });

    if (!suggestion) {
      return;
    }

    if (suggestion.changes.length === 0) {
      this.voiceInfo = "AI не предложил новых изменений по фото";
      return;
    }

    this.aiPhotoSuggestion = suggestion;
  }

  @action.bound
  applyAiPhotoSuggestion(): void {
    const suggestion = this.aiPhotoSuggestion;
    if (!suggestion) {
      return;
    }

    applyAiProductDraftToState({
      draft: suggestion.draft,
      state: this.workflowState,
      availableBoxes: this.availableBoxes,
      formatParentInputValue: (value) => this.formatParentInputValue(value),
    });

    this.aiPhotoSuggestion = null;
    this.voiceInfo = suggestion.usedTranscriptContext
      ? `Черновик применен по фото + тексту (${suggestion.model})`
      : `Черновик применен по фото (${suggestion.model})`;
    this.voiceError = null;
    this.activePhotoIndex = 0;
  }

  @action.bound
  cancelAiPhotoSuggestion(): void {
    this.aiPhotoSuggestion = null;
    this.voiceInfo = "Предложенные AI-правки отклонены";
  }

  @action.bound
  async loadReferenceData(): Promise<void> {
    await loadReferenceDataOperation({
      state: this.workflowState,
      api: this.api,
    });
  }

  @action
  clearForm = (): void => {
    clearAddProductFormOperation({
      state: this.workflowState,
      presetParentId: this.presetParentId,
      stopVoiceListening: () => this.stopVoiceListening(),
    });
    this.activePhotoIndex = 0;
    this.aiPhotoSuggestion = null;
    this.qrPayloadInput = "";
    this.qrBindingError = null;
    this.isQrScannerOpen = false;
    this.isQrScanResolving = false;
  };

  @action
  openCreateBoxWithReturn = (): void => {
    openCreateBoxWithReturnOperation({
      state: this.workflowState,
      navigationStore: this.navigationStore,
    });
  };

  @action
  goBack = (): void => {
    goBackFromAddProductOperation({
      presetParentId: this.presetParentId,
      store: this.store,
      navigationStore: this.navigationStore,
    });
  };

  @action
  submit = async (): Promise<void> => {
    const nextQrPayload = normalizeQrPayloadInput(this.qrPayloadInput);
    if (nextQrPayload && !validateQrPayload(nextQrPayload)) {
      this.qrBindingError = "Укажите QR в формате qr_...";
      return;
    }

    if (nextQrPayload) {
      const isQrAvailable = await this.ensureQrPayloadAvailable(nextQrPayload);
      if (!isQrAvailable) {
        return;
      }
    }

    this.qrBindingError = null;

    await submitAddProductOperation({
      state: this.workflowState,
      api: this.api,
      store: this.store,
      navigationStore: this.navigationStore,
      onCreated: (product) => {
        if (!nextQrPayload) {
          return;
        }

        return this.api
          .bindQrPayload({
            entityId: product.id,
            payload: nextQrPayload,
          })
          .then(() => undefined)
          .catch((error: unknown) => {
            if (error instanceof QrBindingConflictError) {
              this.qrBindingError = this.formatQrConflictMessage(
                nextQrPayload,
                error.existingEntityId,
              );
              return;
            }

            this.qrBindingError =
              error instanceof Error
                ? error.message
                : "Не удалось привязать QR-код к товару";
          });
      },
    });
  };

  private async ensureQrPayloadAvailable(payload: string): Promise<boolean> {
    try {
      const existing = await this.api.resolveQrPayload(payload);
      if (!existing) {
        return true;
      }

      this.qrBindingError = this.formatQrConflictMessage(
        payload,
        existing.entityId,
      );
      return false;
    } catch (error) {
      this.qrBindingError =
        error instanceof Error ? error.message : "Не удалось проверить QR-код";
      return false;
    }
  }

  private formatQrConflictMessage(
    payload: string,
    entityId: string | null,
  ): string {
    if (!entityId) {
      return `QR ${payload} уже используется другой сущностью.`;
    }

    return `QR ${payload} уже привязан к ${entityId}. Выберите другой код.`;
  }

  private formatParentInputValue(value: string): string {
    return formatBoxReferenceInputValue(value, this.availableBoxes);
  }

  private get interactionState(): AddProductInteractionState {
    return this;
  }

  private get workflowState(): AddProductWorkflowState {
    return this;
  }

  private get voiceBindingState(): AddProductVoiceBindingState {
    return this;
  }
}
